import React from 'react';
import { Shield } from 'lucide-react';
import type { Role } from '../../services/security/roles.service';

interface RolesTableSimpleProps {
  roles: Role[];
  isLoading: boolean;
  onViewRole: (role: Role) => void;
}

export const RolesTableSimple: React.FC<RolesTableSimpleProps> = ({
  roles,
  isLoading,
  onViewRole
}) => {

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (activo: boolean) => {
    return activo 
      ? 'bg-green-100 text-green-800 border-green-200' 
      : 'bg-red-100 text-red-800 border-red-200';
  };

  const getStatusText = (activo: boolean) => {
    return activo ? 'Activo' : 'Inactivo';
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
        <span className="ml-2 text-gray-500">Cargando roles...</span>
      </div>
    );
  }

  if (roles.length === 0) {
    return (
      <div className="text-center py-12">
        <Shield className="mx-auto h-16 w-16 text-gray-400 mb-4" />
        <h3 className="text-xs font-medium text-gray-900 mb-2">
          No se encontraron roles
        </h3>
        <p className="text-xs text-gray-500 mb-4">
          No hay roles que coincidan con los criterios de búsqueda
        </p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-2/5">
              Rol
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/5">
              Estado
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/5">
              Módulos
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/5">
              Última Actualización
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {roles.map((role) => (
            <tr
              key={role.id}
              className="hover:bg-gray-50 transition-colors cursor-pointer"
              onClick={() => onViewRole(role)}
            >
              <td className="px-6 py-5 whitespace-nowrap">
                <div className="flex items-center">
                  <div className="flex-shrink-0 h-10 w-10 flex items-center justify-center">
                    <Shield className="h-5 w-5 text-purple-500" />
                  </div>
                  <div className="ml-4">
                    <div className="text-xs font-medium text-gray-900">
                      {role.nombre}
                    </div>
                    {role.descripcion && (
                      <div className="text-xs text-gray-500 truncate max-w-xs">
                        {role.descripcion}
                      </div>
                    )}
                  </div>
                </div>
              </td>
              <td className="px-6 py-5 whitespace-nowrap">
                <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full border ${getStatusColor(role.activo)}`}>
                  {getStatusText(role.activo)}
                </span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-900">
                {role.modulos ? `${role.modulos.length} módulo(s)` : '0 módulos'}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-500">
                {formatDate(role.actualizadoEn)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};